import { Reducer, AnyAction } from 'redux';
import { action, createReducer } from 'typesafe-actions';
import AttachmentsDTO from '@/dtos/attachments/AttachmentsDTO';
import { PageDTO } from '@/dtos/generics';
import SearchQuery from '@/utils/SearchQuery';
import { genericError, genericRequest } from '@/store/modules/utils';
import AttachmentsState from './types';

import CreateAttachmentsDTO from '@/dtos/attachments/CreateAttachmentsDTO';

const INITIAL_STATE: AttachmentsState = {
  data: [],
  next: undefined,
  filters: SearchQuery.build(),
  selected: undefined,
  currentPage: 1,
  prev: undefined,
  total: 0,
  error: false,
  loading: false,
};

export enum AttachmentsTypes {
  SET_LOADING = '@attachments/SET_LOADING',
  SET_FILTERS = '@attachments/SET_FILTERS',
  LIST_REQUEST = '@attachments/LIST_REQUEST',
  LIST_SUCCESS = '@attachments/LIST_SUCCESS',
  LIST_ERROR = '@attachments/LIST_ERROR',
  GET_REQUEST = '@attachments/GET_REQUEST',
  GET_SUCCESS = '@attachments/GET_SUCCESS',
  GET_ERROR = '@attachments/GET_ERROR',
  CREATE_REQUEST = '@attachments/CREATE_REQUEST',
  CREATE_SUCCESS = '@attachments/CREATE_SUCCESS',
  CREATE_ERROR = '@attachments/CREATE_ERROR',
  DELETE_REQUEST = '@attachments/DELETE_REQUEST',
  DELETE_SUCCESS = '@attachments/DELETE_SUCCESS',
  DELETE_ERROR = '@attachments/DELETE_ERROR',
  DOWNLOAD_REQUEST = '@attachments/DOWNLOAD_REQUEST',
  DOWNLOAD_SUCCESS = '@attachments/DOWNLOAD_SUCCESS',
  DOWNLOAD_ERROR = '@attachments/DOWNLOAD_ERROR',
  CLEAR = '@attachments/CLEAR',
}

const AttachmentsActions = {
    setLoading: (loading: boolean) =>
        action(AttachmentsTypes.SET_LOADING, { loading }),
    setFilters: (filters: SearchQuery) =>
        action(AttachmentsTypes.SET_FILTERS, { filters }),

    listRequest: (filters: SearchQuery, page?: number) =>
        action(AttachmentsTypes.LIST_REQUEST, { filters, page }),
    listSuccess: (page: PageDTO<AttachmentsDTO>) =>
        action(AttachmentsTypes.LIST_SUCCESS, page),
    listError: (error: string) => action(AttachmentsTypes.LIST_ERROR, { error }),

    getRequest: (id: string) => action(AttachmentsTypes.GET_REQUEST, id),
    getSuccess: (resource: AttachmentsDTO) =>
        action(AttachmentsTypes.GET_SUCCESS, resource),
    getError: (error: string) => action(AttachmentsTypes.GET_ERROR, { error }),

    createRequest: (data: CreateAttachmentsDTO) =>
        action(AttachmentsTypes.CREATE_REQUEST, data),
    createSuccess: (resource: AttachmentsDTO) =>
        action(AttachmentsTypes.CREATE_SUCCESS, resource),
    createError: (error: string) => action(AttachmentsTypes.CREATE_ERROR, { error }),

    deleteRequest: (id: string) =>
        action(AttachmentsTypes.DELETE_REQUEST, id),
    deleteSuccess: (id: string) => action(AttachmentsTypes.DELETE_SUCCESS, { id }),
    deleteError: (error: string) => action(AttachmentsTypes.DELETE_ERROR, { error }),

    downloadRequest: (id: string, filename: string) => action(AttachmentsTypes.DOWNLOAD_REQUEST, { id, filename }),
    downloadSuccess: () => action(AttachmentsTypes.DOWNLOAD_SUCCESS),
    downloadError: (error: string) => action(AttachmentsTypes.DOWNLOAD_ERROR, { error }),

    clear: () => action(AttachmentsTypes.CLEAR),
}

export default AttachmentsActions;

type AttachmentsReducer<Action extends AnyAction> = Reducer<AttachmentsState, Action>;

const setLoading: AttachmentsReducer<
    ReturnType<typeof AttachmentsActions.setLoading>
> = (state = INITIAL_STATE, { payload }) => ({
    ...state,
    loading: payload.loading,
});

const setFilters: AttachmentsReducer<
    ReturnType<typeof AttachmentsActions.setFilters>
> = (state = INITIAL_STATE, { payload }) => ({
    ...state,
    filters: payload.filters,
});

const listSuccess: AttachmentsReducer<
    ReturnType<typeof AttachmentsActions.listSuccess>
> = (state = INITIAL_STATE, { payload }) => ({
    ...state,
    data: payload.data,
    currentPage: payload.currentPage,
    next: payload.next,
    prev: payload.prev,
    total: payload.total,
    loading: false,
});

const getSuccess: AttachmentsReducer<
    ReturnType<typeof AttachmentsActions.getSuccess>
> = (state = INITIAL_STATE, { payload }) => ({
    ...state,
    selected: payload,
    loading: false,
});

const createSuccess: AttachmentsReducer<
    ReturnType<typeof AttachmentsActions.createSuccess>
> = (state = INITIAL_STATE, { payload }) => ({
    ...state,
    data: [...state.data, payload],
    loading: false,
});

const deleteSuccess: AttachmentsReducer<
    ReturnType<typeof AttachmentsActions.deleteSuccess>
> = (state = INITIAL_STATE, { payload }) => ({
    ...state,
    data: state.data.filter(attachment => attachment.id !== payload.id),
    loading: false,
});

const clear: AttachmentsReducer<ReturnType<typeof AttachmentsActions.clear>> = () =>
    INITIAL_STATE;

export const reducer = createReducer<AttachmentsState>(INITIAL_STATE)
    .handleAction(AttachmentsTypes.SET_LOADING, setLoading)
    .handleAction(AttachmentsTypes.SET_FILTERS, setFilters)
    .handleAction(AttachmentsTypes.LIST_REQUEST, genericRequest)
    .handleAction(AttachmentsTypes.LIST_SUCCESS, listSuccess)
    .handleAction(AttachmentsTypes.LIST_ERROR, genericError)
    .handleAction(AttachmentsTypes.GET_REQUEST, genericRequest)
    .handleAction(AttachmentsTypes.GET_SUCCESS, getSuccess)
    .handleAction(AttachmentsTypes.GET_ERROR, genericError)
    .handleAction(AttachmentsTypes.CREATE_REQUEST, genericRequest)
    .handleAction(AttachmentsTypes.CREATE_SUCCESS, createSuccess)
    .handleAction(AttachmentsTypes.CREATE_ERROR, genericError)
    .handleAction(AttachmentsTypes.DELETE_REQUEST, genericRequest)
    .handleAction(AttachmentsTypes.DELETE_SUCCESS, deleteSuccess)
    .handleAction(AttachmentsTypes.DELETE_ERROR, genericError)
    .handleAction(AttachmentsTypes.DOWNLOAD_REQUEST, genericRequest)
    .handleAction(AttachmentsTypes.DOWNLOAD_SUCCESS, genericRequest)
    .handleAction(AttachmentsTypes.DOWNLOAD_ERROR, genericError)
    .handleAction(AttachmentsTypes.CLEAR, clear);